import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const TABS = [
    { id: "all", label: "Everything" },
    { id: "snacks", label: "Snacks" },
    { id: "drinks", label: "Drinks" },
    { id: "fresh", label: "Fresh" },
];

const ITEMS = [
    {
        id: "chips",
        cat: "snacks",
        title: "Chips & crisps",
        note: "Baked and classic favorites",
        img: "https://images.unsplash.com/photo-1566478989037-eec170784d0b?auto=format&fit=crop&w=900&q=85",
        alt: "Bags of baked chips and crisps",
    },
    {
        id: "sodas",
        cat: "drinks",
        title: "Sodas & sparkling",
        note: "Ice-cold at 36°F, always stocked",
        img: "https://images.unsplash.com/photo-1554866585-cd94860890b7?auto=format&fit=crop&w=900&q=85",
        alt: "Ice-cold soda can with condensation",
    },
    {
        id: "energy",
        cat: "drinks",
        title: "Energy drinks",
        note: "For the afternoon push",
        img: "https://images.unsplash.com/photo-1622543925917-763c34d1a86e?auto=format&fit=crop&w=900&q=85",
        alt: "Chilled energy drink can on ice",
    },
    {
        id: "sandwiches",
        cat: "fresh",
        title: "Pre-wrapped deli sandwiches",
        note: "Made fresh, sealed daily",
        img: "https://images.unsplash.com/photo-1528735602780-2552fd46c7af?auto=format&fit=crop&w=900&q=85",
        alt: "Fresh deli sandwiches",
    },
    {
        id: "bars",
        cat: "snacks",
        title: "Protein & snack bars",
        note: "Grab-and-go fuel",
        img: "https://images.unsplash.com/photo-1590080875515-8a3a8dc5735e?auto=format&fit=crop&w=900&q=85",
        alt: "Chocolate-dipped snack bars",
    },
];

export default function Showcase() {
    const [tab, setTab] = useState("all");
    const visible = ITEMS.filter((i) => tab === "all" || i.cat === tab);

    return (
        <section id="snacks" data-testid="showcase-section" className="relative py-24 sm:py-32 bg-[#F1EFE6]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
                    <div>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.7, ease: EASE }}
                            className="font-mono-brand text-xs tracking-[0.25em] uppercase text-[#3FAE4A] mb-5"
                            data-testid="showcase-eyebrow"
                        >
                            Inside the machine
                        </motion.p>
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
                            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1] tracking-tight text-[#0E1E12] max-w-xl"
                            data-testid="showcase-heading"
                        >
                            Food your team will actually look forward to
                        </motion.h2>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
                        className="flex flex-wrap gap-2"
                        data-testid="showcase-tabs"
                    >
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                data-testid={`showcase-tab-${t.id}`}
                                onClick={() => setTab(t.id)}
                                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                                    tab === t.id
                                        ? "bg-[#0E1E12] text-white shadow-[0_8px_20px_-8px_rgba(14,30,18,0.5)]"
                                        : "bg-white/70 text-[#4A584C] hover:bg-white hover:text-[#0E1E12] border border-[#1B4D1B]/10"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </motion.div>
                </div>

                <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="product-showcase-grid">
                    <AnimatePresence mode="popLayout">
                        {visible.map((item, i) => (
                            <motion.article
                                layout
                                key={item.id}
                                data-testid={`showcase-item-${item.id}`}
                                initial={{ opacity: 0, y: 40, scale: 0.96 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.94 }}
                                transition={{ duration: 0.6, delay: i * 0.06, ease: EASE }}
                                className={`group spotlight-frame rounded-3xl bg-white border border-[#1B4D1B]/10 overflow-hidden transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-[#3FAE4A]/40 hover:shadow-[0_30px_60px_-20px_rgba(14,30,18,0.25)] ${
                                    i % 3 === 1 ? "lg:translate-y-8" : ""
                                }`}
                                onMouseMove={(e) => {
                                    const r = e.currentTarget.getBoundingClientRect();
                                    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                                    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                                }}
                            >
                                <div className="h-60 overflow-hidden">
                                    <img
                                        src={item.img}
                                        alt={item.alt}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110"
                                    />
                                </div>
                                <div className="p-6">
                                    <p className="font-mono-brand text-[10px] tracking-[0.25em] uppercase text-[#F58220] mb-2">
                                        {item.cat}
                                    </p>
                                    <h3 className="font-display text-xl font-semibold text-[#0E1E12]">{item.title}</h3>
                                    <p className="text-sm text-[#768578] mt-1">{item.note}</p>
                                </div>
                            </motion.article>
                        ))}
                    </AnimatePresence>
                </motion.div>
            </div>
        </section>
    );
}
